import { Text, View, useWindowDimensions } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import CardGrid from '@/components/General/CardGrid';
import SmallGroupCard from '@/components/SmallGroups/SmallGroupCard';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import usePlatform from '@/utils/hooks/usePlatform';
import { useEffect, useState, useMemo } from 'react';
import { useRouter, useNavigation, useLocalSearchParams } from 'expo-router';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import { Table } from '@/components/General/Table';
import Avatar from '@/components/People/Avatar';
import {Tooltip} from '@/components/General/Tooltip';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import TableFilters from '@/components/General/TableFilters';
import Unicon from '@/components/General/Unicon';
import { saveSmallGroup } from '@/data/smallGroups';
import SmallGroupSessionsTable from '@/components/SmallGroups/SmallGroupSessionsTable';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import { TabView } from 'react-native-tab-view';
import ReachTabBar from '@/components/General/ReachTabBar';
import PageTitle from '@/components/General/PageTitle';
import useSaveShortcut from '@/utils/hooks/useSaveShortcut';
import useBibleStudies from '@/utils/hooks/useBibleStudies';

export default function SmallGroupsScreen(){
    
    /*** State ***/
    const [selectedType, setSelectedType] = useState(null);
    const [searchText, setSearchText] = useState("")
    const [tabIndex, setTabIndex] = useState(0);
    const typeName = selectedType?.name || 'Small Group';
    const typeNamePlural = typeName.endsWith('y') ? typeName.slice(0, -1) + 'ies' : typeName + 's';

    const tabRoutes = useMemo(() => [
        { key: 'studies', title: typeNamePlural },
        { key: 'sessions', title: 'Sessions' }
    ], [typeNamePlural]);
    const [sortConfig, setSortConfig] = useState(null);
    const layout = useWindowDimensions();
    
    /*** Theme & Dimensions ***/
    const {theme: {colors, fonts}} = useUnistyles();
    const { isLandscape, isNative } = usePlatform();
    const router = useRouter();
    const navigation = useNavigation();
    const searchParams = useLocalSearchParams();

    const {data: loggedInUser, isLoading: isLoadingLoggedInUser} = Query.people.loggedInUser()
    const saveShortcut = useSaveShortcut(loggedInUser?.id);
    const { enabled: bibleStudiesEnabled } = useBibleStudies();
    const [filters, setFilters] = useState([])

    const queryFilters = useMemo(() => {
        const base = filters.map(f => ({ id: f.id, value: f.id === 'leader' ? (f.value ? { id: f.value.id } : null) : f.value }));
        if (selectedType?.id) {
            base.push({ id: 'smallGroupType', value: { id: selectedType.id } });
        }
        return base;
    }, [filters, selectedType]);
    const {data: smallGroups, isLoading, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } = Query.smallGroups.all({filters: queryFilters, searchTerm: searchText, sortKey: sortConfig?.key, sortDirection: sortConfig?.direction, options: {enabled: !isLoadingLoggedInUser}});
    const { data: smallGroupTypes } = Query.smallGroupTypes.all({});

    const [suggestedSearch, setSuggestedSearch] = useState('');
    
    const defaultSmallGroupType = smallGroupTypes?.find(t => t.name === 'Small Group');
    const roleID = selectedType?.Role?.id || defaultSmallGroupType?.Role?.id;

    const suggestedTeachersQuery = Query.people.all({
        filters: roleID ? { conditions: [{ fieldId: 'roles', operator: 'contains_any', value: [roleID] }] } : undefined,
        filterLogic: 'AND',
        searchTerm: suggestedSearch,
        options: { enabled: roleID != null }
    });

    const saveSmallGroupMutation = useMutation({
        mutationFn: saveSmallGroup,
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['smallGroups']});
            queryClient.invalidateQueries({queryKey: ['dashboardData']});
            queryClient.invalidateQueries({queryKey: ['people']});
        }
    })

    /*** Queries ***/
    const queryClient = useQueryClient()


    const renderItem = ({item}) => {
        return <SmallGroupCard smallGroup={item} />
    }

    const updateFilterValue = (filterId, value) => {
        setFilters(prev => prev.map(f => f.id === filterId ? {...f, value} : f));
    };

    const filtersWithComponents = filters.map(f => {
        if(f.id === "leader"){
            return {...f, component: (
                <View style={styles.teacherFilterContainer}>
                    <SheetSelector
                        titleOnly
                        type="people"
                        queryResult={roleID ? suggestedTeachersQuery : undefined}
                        queryResultSearch={setSuggestedSearch}
                        title="Leader"
                        value={f.value}
                        onChange={(selected) => updateFilterValue("leader", selected)}
                    />
                </View>
            )};
        }
        return f;
    });

    useEffect(() => {
        navigation.setOptions({
            headerTitle: () => <PageTitle title={`${typeNamePlural}`} />
        });
    }, [navigation, typeNamePlural])

    useEffect(()=> {
        if(loggedInUser){
            // Determine initial values from URL params or defaults
            const leaderFromUrl = searchParams.leaderID && searchParams.leaderName 
                ? { id: searchParams.leaderID, firstName: searchParams.leaderName.split(' ')[0], lastName: searchParams.leaderName.split(' ').slice(1).join(' ') }
                : loggedInUser;
            const statusFromUrl = searchParams.status || "ACTIVE";

            setFilters([
                {
                    id: "leader",
                    inputType: "component",
                    label: "Leader",
                    value: leaderFromUrl
                },
                {
                    id: "status",
                    inputType: "dropdown",
                    label: "Status",
                    value: statusFromUrl,
                    options: [
                        {label: "Active", value: "ACTIVE"},
                        {label: "Canceled", value: "CANCELED"},
                        {label: "Completed", value: "COMPLETED"},
                        {label: "Paused", value: "PAUSED"}
                    ]
                },
            ])
        }
    }, [loggedInUser])


    useEffect(() => {
        if (smallGroupTypes && searchParams.typeID) {
            const type = smallGroupTypes.find(t => t.id === searchParams.typeID);
            setSelectedType(type || null);
        } else {
            setSelectedType(null);
        }
    }, [smallGroupTypes, searchParams.typeID]);

    useEffect(() => {
        if (loggedInUser && selectedType) {
            // Flag-on: Bible Study types live in the Bible Studies module —
            // deep links to them re-route instead of rendering legacy UI.
            if (bibleStudiesEnabled && selectedType.purpose === 'BIBLE_STUDY') {
                router.replace('/biblestudies');
                return;
            }
            const hasAccess = !selectedType.roleID || loggedInUser?.isAdmin || loggedInUser?.Roles?.some(r => r.id === selectedType.roleID);
            if (!hasAccess) {
                router.canGoBack() ? router.back() : router.replace("/");
            }
        }
    }, [loggedInUser, selectedType, router, bibleStudiesEnabled]);

    useEffect(() => {
        const options = []

        if (selectedType?.id) {
            options.push({
                name: "Save to Shortcuts",
                icon: dropdownIcons.favorite,
                onSelect: () => saveShortcut({ screen: `/smallgroups?typeID=${selectedType.id}`, label: typeNamePlural, icon: 'book-outline' })
            });
        }

        if(loggedInUser?.can?.("Create Small Groups")) options.push({name:`New ${typeName}`, onSelect: () => router.push({pathname: "/smallgroups/create", params: {typeID: selectedType?.id || ''}})})

        if(options.length > 0){
            navigation.setOptions({
                title: typeNamePlural,
                headerRight: () => <DropdownMenu testID="HeaderMenu" options={options} />
            })
        }
    }, [navigation, loggedInUser, typeName, typeNamePlural, selectedType, saveShortcut])

    const flatSmallGroups = useMemo(() => {
        if (!smallGroups) return [];
        return smallGroups.pages.flatMap(page => page.data).map(group => ({
            ...group,
            lastTaught: group.Event?.EventInstances?.[0]?.startTime
        }));
    }, [smallGroups]);

    const statuses = ["Active", "Completed", "Canceled", "Paused"];

    const getStatusComponent = (status, id) => {
        const color = status == "ACTIVE" ? {text:"success", background:"successContainer"} 
            : (status == "CANCELED") ? {text:"error", background:"errorContainer"} 
            : (status == "COMPLETED") ? {text:"info", background:"onInfo"} 
            : (status == "PAUSED") ? {text:"warning", background:"warningContainer"} 
            : {text:"text", background:"textSecondary"};

        return <DropdownMenu
                shouldStopPropagation
                options={statuses.map(status => ({name: status, disabled: status.toUpperCase() == status, onSelect: (option) => saveSmallGroupMutation.mutate({id: id, status: option.name.toUpperCase(), lastStatusChange: new Date()}) }))}
                triggerComponent={<View style={styles.pill(color.background, color.text)}>
            <Text style={styles.pillText(color.text)}>{status}</Text>
            <Unicon name="chevron-forward-outline" size={18} colorVariant={color.text}/>
        </View>}
            />
    }
    
    return (
        <View style={styles.container}>
            <TabView
                swipeEnabled={isNative}
                navigationState={{ index: tabIndex, routes: tabRoutes }}
                onIndexChange={setTabIndex}
                initialLayout={{ width: layout.width }}
                renderTabBar={(props) => <ReachTabBar {...props} />}
                lazy
                renderScene={({ route }) => {
                    switch (route.key) {
                        case 'sessions':
                            return (
                                <View style={styles.tableContainer}>
                                    <SmallGroupSessionsTable
                                        smallGroupTypeID={selectedType?.id}
                                        smallGroupTypeName={selectedType?.name}
                                        title={`${selectedType?.name} Sessions`}
                                        description={`All individual ${selectedType?.name} sessions. Use the filters and search bar to find specific sessions.`}
                                        roleID={roleID}
                                    />
                                </View>
                            );
                        case 'studies':
                            return (
                                <View style={styles.listContainer}>
                {isNative && isLandscape
                    ? <View style={styles.tableContainer}><Table
                        keyExtractor={(item) => item.id}
                        onRefresh={() => queryClient.invalidateQueries({queryKey: QueryKeys.smallGroups.all()})}
                        title={filters.find(f => f.id == "leaderID")?.value == loggedInUser?.id ? `My ${typeNamePlural}` : `All ${typeNamePlural}`}
                        description={filters.find(f => f.id == "leaderID")?.value == loggedInUser?.id ? `View and manage your ${typeNamePlural}` : `View all ${typeNamePlural} in your organization. ${isNative ? "Tap" : "Click"} on a ${typeName} to view information and all sessions.`}
                        filters={filtersWithComponents}
                        onFiltersChange={(newFilters) => {
                            setFilters(newFilters);
                            const leader = newFilters.find(f => f.id === "leader")?.value;
                            const status = newFilters.find(f => f.id === "status")?.value;
                            router.setParams({
                                leaderID: leader?.id || '',
                                leaderName: leader ? `${leader.firstName} ${leader.lastName}`.trim() : '',
                                status: status || ''
                            });
                        }}
                        onClearFilters={() => {
                            const clearedFilters = filters.map(f => ({...f, value: null}));
                            setFilters(clearedFilters);
                            router.setParams({ leaderID: '', leaderName: '', status: '' });
                        }}
                        onSearchChange={setSearchText}
                        isLoading={isLoading || isFetching}
                        onRowClick={(smallGroup) => router.push("/smallgroups/" + smallGroup.id)}
                        data={isLoading ? [] : flatSmallGroups}
                        onEndReachedThreshold={0.1}
                        onEndReached={() => {
                            if(!isLoading && !isFetchingNextPage && hasNextPage) fetchNextPage()
                        }}
                        actions={[
                            {
                                name: "Log a Session",
                                icon: {web: 'add'},
                                onSelect: (smallGroup) => {
                                    router.push({pathname: "/smallgroups/sessions/create", params: {smallGroupID: smallGroup.id}})
                                }
                            },
                            {
                                name: "Edit",
                                icon: {web: 'create-outline'},
                                onSelect: (smallGroup) => router.push({pathname: "/smallgroups/create", params: {id: smallGroup.id}})
                            },
                            // {
                            //     name: "Delete",
                            //     destructive: true,
                            //     onSelect: (smallGroup) => deleteSmallGroup.mutate(smallGroup.id)
                            // }
                        ]}
                        sortConfig={sortConfig}
                        onSortChange={setSortConfig}
                        columns={[
                            {accessor: "name", value: "Name", sortable: true},
                            // {accessor: "SmallGroupType.name", value: "Type", sortable: true},
                            {accessor: "lastTaught", value: "Last Taught", sortable: true, renderer: (group) => group.lastTaught ? new Date(group.lastTaught).toLocaleDateString() : "Never"},
                            {accessor: "status", value: "Status", sortable: true, renderer: (group) => getStatusComponent(group.status, group.id)},
                            {accessor: "leader", value: "Leader(s)", sortable: true, renderer: (group) => (
                                <View style={{flexDirection:"row", alignItems:"center", gap:5}}>
                                    <Avatar profile={group.Leader} size={30} disableBorder/>
                                    <Text style={{color:colors.text, fontSize:12, fontFamily:fonts.heading}}>{group.Leader?.firstName} {group.Leader?.lastName}</Text>
                                </View>
                            )},
                            {accessor: "coLeader", value: "", renderer: (group) => group.CoLeader ? (
                                <View style={{flexDirection:"row", alignItems:"center", gap:5}}>
                                    <Avatar profile={group.CoLeader} size={30} disableBorder/>
                                    <Text style={{color:colors.text, fontSize:12, fontFamily:fonts.heading}}>{group.CoLeader?.firstName} {group.CoLeader?.lastName}</Text>
                                </View>
                            ) : (
                                <></>
                            )},
                            {accessor: "students", value: "Members", renderer: (group) => (
                                <View style={{flexDirection:"row", alignItems:"center", gap:5}}>
                                    {group.Students?.slice(0, 3).map((student, i) => (
                                        <View key={student.Person?.id} style={{marginLeft:i > 0 ? -15 : 0}}>
                                            <Avatar profile={student.Person} size={30} disableBorder/>
                                        </View>
                                    ))}
                                    
                                    {group.Students?.length > 3 && (
                                        <View style={{marginLeft:-15}}>
                                            <Tooltip title={group.Students?.slice(3).map(student => student.Person?.firstName + " " + student.Person?.lastName).join(", ")}>
                                                <View style={{backgroundColor:colors.primary, paddingVertical:2, paddingHorizontal:4, borderRadius:30, height:30, width:30, alignItems:"center", justifyContent:"center"}}>
                                                    <Text style={{color:colors.onPrimary, fontSize:12, fontFamily:fonts.heading}}>+{group.Students?.length - 3}</Text>
                                                </View>
                                            </Tooltip>
                                        </View>
                                    )}
                                </View>
                            )},
                        ]} 
                    /></View>
                    : (<View style={styles.listContainerMobile}>
                    <TableFilters tableName={typeNamePlural} filters={filtersWithComponents} onFiltersChange={(newFilters) => {
                            setFilters(newFilters);
                            const leader = newFilters.find(f => f.id === "leader")?.value;
                            const status = newFilters.find(f => f.id === "status")?.value;
                            router.setParams({
                                leaderID: leader?.id || '',
                                leaderName: leader ? `${leader.firstName} ${leader.lastName}`.trim() : '',
                                status: status || ''
                            });
                        }} onClearFilters={() => {
                            const clearedFilters = filters.map(f => ({...f, value: null}));
                            setFilters(clearedFilters);
                            router.setParams({ leaderID: '', leaderName: '', status: '' });
                        }} onSearchChange={setSearchText}/>
                        <CardGrid
                            keyExtractor={(item) => item.id}
                            estimatedItemSize={310}
                            columns={{xs: 2, md: 3, xl: 4}}
                            gap={12}
                            padding={{paddingTop: 10}}
                            // contentInsetAdjustmentBehavior="automatic"
                            data={isLoading ? [] : flatSmallGroups}
                            renderItem={renderItem}
                            onEndReachedThreshold={0.1}
                            onEndReached={() => {
                                if(!isLoading && !isFetchingNextPage && hasNextPage) fetchNextPage()
                            }}
                            refreshing={isFetching && !isLoading && !isFetchingNextPage}
                            onRefresh={() => {
                                queryClient.invalidateQueries({queryKey: QueryKeys.smallGroups.all()});
                            }}
                            ListFooterComponent={!isLoading
                                ? <View style={{paddingBottom:80}}>{
                                    flatSmallGroups?.length == 0 ? <EmptyListPlaceholder icon="chatbox-ellipses-outline" spacing={10} message={`No ${typeNamePlural} yet!`}/>
                                    : isFetchingNextPage ? <ActivityIndicator size="small" color={colors.primary} /> : <View></View>
                                }
                                </View>
                                : <View style={{alignContent:'center', justifyContent:'center'}}>
                                    <ActivityIndicator size="large" color={colors.primary} />
                                </View>
                            }
                        />
                    </View>)
                }
                                </View>
                            );
                        default:
                            return null;
                    }
                }}
            />
        </View>
    )
}


const styles = StyleSheet.create(({colors, fonts}) => ({
    container:{
        flex:1,
    },
    header:{
        flexDirection:"row", 
        justifyContent:"space-between", 
        alignItems:"center", 
        width:"100%"
    },
    card:{
        borderRadius:8,
        padding: 20,
        paddingBottom: 15,
        overflow:"hidden",
        backgroundColor:colors.surface,
    },
    listContainer:{
        flex:1,
        width:"100%",
    },
    listContainerMobile:{
        flex:1,
        padding:10,
    },
    tableContainer: {
        flex:1,
        paddingTop:10
    },
    button:{
        flexGrow:1, 
        borderRadius:8, 
        backgroundColor: colors.surface, 
        height:50, 
        flexDirection:"row", 
        justifyContent:"space-between", 
        alignItems:"center", 
        paddingHorizontal:10
    },
    pill:(statusColor, statusTextColor) => ({
        paddingVertical:3,
        paddingHorizontal:10, 
        margin:3,
        borderWidth:1,
        borderColor: colors[statusTextColor],
        backgroundColor:colors[statusColor], 
        borderRadius:8,
        width:"fit-content",
        flexDirection:"row", 
        alignItems:"center"
    }),
    pillText:(color) => ({
        fontSize:13, 
        color:colors[color], 
        fontFamily:fonts.bold, 
        textAlign:'center'
    }),
    teacherFilterContainer: {
        gap: 8,
    }
}))
